import { useState, useRef, useEffect, useMemo } from 'react'
import { Outlet, useNavigate, Link } from 'react-router-dom'
import {
  FiHome, FiClock, FiCalendar, FiUsers, FiBarChart2, FiBriefcase, FiSettings,
  FiMapPin, FiLayers, FiShield, FiTrendingUp, FiGlobe, FiLogOut, FiChevronDown,
  FiLock, FiKey, FiDownload, FiMonitor,
} from 'react-icons/fi'
import ActivarQuioscoModal from '../../components/ActivarQuioscoModal'
import { useAuth } from '../../contexts/AuthContext'
import { canAccess, getEffectivePlan } from '../../config/planFeatures'
import { useNotificaciones } from '../../hooks/useNotificaciones'
import { useDiasSinJustificar } from '../../hooks/useDiasSinJustificar'
import { useCorreccionesPropuestas } from '../../hooks/useCorreccionesPropuestas'
import NotificationBell from '../../components/NotificationBell'
import TrialBanner from '../../components/TrialBanner'
import ModalDiasSinJustificar from '../../components/v2/ModalDiasSinJustificar'
import ModalCorreccionesPropuestas from '../../components/v2/ModalCorreccionesPropuestas'
import ModalCambiarPasswordV2 from '../../components/v2/ModalCambiarPasswordV2'
import ModalCambiarPinV2 from '../../components/v2/ModalCambiarPinV2'
import AppShellV2 from '../../layouts/AppShellV2'
import { Avatar } from '../../ui'

/*
 * AppLayoutV2 — layout completo de la app rediseñada bajo /v2.
 * Replica la lógica de Fichaje.jsx (items por rol/plan/tipo, banner trial,
 * notificaciones, logout) usando AppShellV2 como shell visual.
 *
 * Notas:
 *   - Modales de cambio de PIN/password se omiten en esta v1 — el usuario
 *     puede usar Fichaje.jsx (rutas legacy) si lo necesita.
 *   - Mobile: el sidebar de AppShellV2 no tiene drawer mobile; en pantallas
 *     pequeñas la sidebar sigue siendo de 240/64 px (mejorable en v2).
 */

const BASE = '/v2'

// Construye el href absoluto bajo /v2
const v2 = (path) => `${BASE}${path === '/' ? '' : path}`.replace(/\/$/, '') || BASE

function UserActions({ empleado, isAsesoriaAdmin, onLogout, onCambiarPassword, onCambiarPin, onActivarQuiosco }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const rolLabel = empleado?.rol === 'super_admin' ? 'Super Administrador'
                 : empleado?.rol === 'admin'       ? 'Administrador'
                 : 'Empleado'

  // Super admin no usa PIN — el flujo de fichaje táctil no le aplica
  const mostrarPin = empleado?.rol !== 'super_admin'
  // Modo quiosco: solo admin de empresa normal (no asesoría, no super_admin)
  const mostrarQuiosco = empleado?.rol === 'admin' && !isAsesoriaAdmin

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2.5 p-1.5 rounded-soft hover:bg-[var(--bg-2)] text-left"
      >
        <Avatar name={empleado?.nombre || 'U'} size={28} />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-medium truncate">{empleado?.nombre}</div>
          <div className="text-[10.5px] font-mono text-[var(--ink-3)] truncate">{rolLabel}</div>
        </div>
        <FiChevronDown size={13} className={`text-[var(--ink-3)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 ui-card overflow-hidden z-30">
          <button
            type="button"
            onClick={() => { setOpen(false); onCambiarPassword() }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition-colors"
          >
            <FiLock size={14} className="text-[var(--ink-3)]" />
            Cambiar contraseña
          </button>
          {mostrarPin && (
            <button
              type="button"
              onClick={() => { setOpen(false); onCambiarPin() }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition-colors"
            >
              <FiKey size={14} className="text-[var(--ink-3)]" />
              {empleado?.has_pin ? 'Cambiar PIN' : 'Asignar PIN'}
            </button>
          )}
          {empleado?.rol !== 'super_admin' && (
            <Link
              to="/v2/mis-datos"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition-colors"
            >
              <FiDownload size={14} className="text-[var(--ink-3)]" />
              Mis datos (RGPD)
            </Link>
          )}
          {mostrarQuiosco && (
            <button
              type="button"
              onClick={() => { setOpen(false); onActivarQuiosco() }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition-colors"
            >
              <FiMonitor size={14} className="text-[var(--ink-3)]" />
              Activar modo quiosco
            </button>
          )}
          <div className="h-px bg-[var(--line)]" />
          <button
            type="button"
            onClick={() => { setOpen(false); onLogout() }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[#C13030] hover:bg-[rgba(239,68,68,.06)]"
          >
            <FiLogOut size={14} />
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  )
}

export default function AppLayoutV2() {
  const { empleado, logout, empresaBloqueada, empresaInfo, isAsesoria, refreshEmpleado } = useAuth()
  const navigate = useNavigate()
  const {
    count: notifCount,
    notificaciones,
    loading: notifLoading,
    fetchNotificaciones,
    marcarLeida,
    marcarTodasLeidas,
  } = useNotificaciones(empleado)
  const { diasPendientes, recargar: recargarDiasPendientes } = useDiasSinJustificar(empleado)
  const { propuestasPendientes, recargar: recargarPropuestas } = useCorreccionesPropuestas(empleado)
  const [mostrarModalDiasPendientes, setMostrarModalDiasPendientes] = useState(false)
  const [mostrarModalPropuestas, setMostrarModalPropuestas] = useState(false)
  const [mostrarModalPassword, setMostrarModalPassword] = useState(false)
  const [mostrarModalPin, setMostrarModalPin] = useState(false)
  const [mostrarModalQuiosco, setMostrarModalQuiosco] = useState(false)

  const plan = getEffectivePlan(empresaInfo)
  const isAsesoriaAdmin = empleado?.rol === 'admin' && isAsesoria

  // Handler para navegación desde notificaciones (replica Fichaje.jsx)
  const handleNotificationNavigate = (notif) => {
    const isAdmin = empleado?.rol === 'admin'
    switch (notif.tipo) {
      case 'fichaje_incompleto':
      case 'fichaje_olvidado':
        navigate(isAdmin ? v2('/gestion?tab=correcciones') : v2('/historial'))
        break
      case 'correccion_solicitada':
        navigate(v2('/gestion?tab=correcciones'))
        break
      case 'correccion_aprobada':
      case 'correccion_rechazada':
        navigate(v2('/historial'))
        break
      case 'ausencia_solicitada':
        navigate(v2('/gestion?tab=ausencias'))
        break
      case 'ausencia_aprobada':
      case 'ausencia_rechazada':
        navigate(v2('/ausencias'))
        break
      case 'correccion_propuesta_admin':
        navigate(v2('/historial'))
        setTimeout(() => setMostrarModalPropuestas(true), 300)
        break
      case 'correccion_propuesta_aceptada':
      case 'correccion_propuesta_rechazada':
        navigate(v2('/gestion?tab=correcciones'))
        break
      case 'fichaje_en_ausencia':
        navigate(v2('/gestion?tab=ausencias'))
        break
      default:
        break
    }
  }

  // Popup días sin justificar al login (solo Avanzado)
  useEffect(() => {
    if (empleado && diasPendientes.length > 0 && canAccess(plan, 'notificaciones_auto')) {
      const loginTimestamp = sessionStorage.getItem('login_timestamp')
      const clave = `ultimo_aviso_dias_pendientes_${empleado.id}`
      const ultimoAvisoTimestamp = localStorage.getItem(clave)
      if (loginTimestamp && (!ultimoAvisoTimestamp || parseInt(loginTimestamp) > parseInt(ultimoAvisoTimestamp))) {
        setMostrarModalDiasPendientes(true)
        localStorage.setItem(clave, Date.now().toString())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empleado, diasPendientes])

  // Popup propuestas de corrección al login (solo Avanzado)
  useEffect(() => {
    if (empleado && propuestasPendientes.length > 0 && canAccess(plan, 'correcciones_flujo')) {
      const loginTimestamp = sessionStorage.getItem('login_timestamp')
      const clave = `ultimo_aviso_propuestas_correccion_${empleado.id}`
      const ultimoAvisoTimestamp = localStorage.getItem(clave)
      if (loginTimestamp && (!ultimoAvisoTimestamp || parseInt(loginTimestamp) > parseInt(ultimoAvisoTimestamp))) {
        setMostrarModalPropuestas(true)
        localStorage.setItem(clave, Date.now().toString())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empleado, propuestasPendientes])

  // Items según rol / plan / tipo (replica la lógica de Fichaje.jsx)
  const { navPrimary, navSecondary } = useMemo(() => {
    // Super admin
    if (empleado?.rol === 'super_admin') {
      return {
        navPrimary: [
          { id: 'empresas',        icon: FiBriefcase, label: 'Empresas',         to: v2('/empresas') },
          { id: 'usuarios-global', icon: FiGlobe,     label: 'Usuarios globales', to: v2('/usuarios-global') },
          { id: 'leads',           icon: FiBriefcase, label: 'Leads',            to: v2('/leads') },
          { id: 'sistema',         icon: FiSettings,  label: 'Sistema',          to: v2('/sistema') },
        ],
        navSecondary: [],
      }
    }

    // Asesoría admin
    if (isAsesoriaAdmin) {
      return {
        navPrimary: [
          { id: 'mis-clientes', icon: FiBriefcase, label: 'Mis clientes', to: v2('/mis-clientes') },
          { id: 'gestores',     icon: FiUsers,     label: 'Gestores',     to: v2('/empleados') },
        ],
        navSecondary: [],
      }
    }

    // Empleado / admin común
    const personal = [
      { id: 'mi-fichaje',    icon: FiClock,        label: 'Mi fichaje',     to: v2('/'), end: true },
      { id: 'historial',     icon: FiCalendar,     label: 'Mi historial',   to: v2('/historial') },
      ...(canAccess(plan, 'ausencias') ? [
        { id: 'ausencias',   icon: FiCalendar,     label: 'Mis permisos',   to: v2('/ausencias') },
      ] : []),
      { id: 'horas-extras',  icon: FiTrendingUp,   label: 'Mis horas extras', to: v2('/mis-horas-extras') },
      { id: 'fichaje-grupal', icon: FiUsers,       label: 'Fichaje grupal',   to: v2('/fichaje-grupal') },
    ]

    if (empleado?.rol !== 'admin') {
      return { navPrimary: personal, navSecondary: [] }
    }

    // Admin: añade gestión + reportes en primary, sub-items de Datos en secondary
    // adminBadge marca visualmente que estos items son privativos de admin (igual que en la legacy)
    const admin = [
      { id: 'gestion',  icon: FiHome,       label: 'Gestión fichajes', to: v2('/gestion'),  adminBadge: true },
      { id: 'reportes', icon: FiBarChart2,  label: 'Reportes',         to: v2('/reportes'), adminBadge: true },
      // Ambos dependen de features del plan Avanzado: el calendario de
      // vacaciones lee /api/ausencias y el mapa lee /api/geolocalizacion, así
      // que en Pro llevaban a pantallas vacías sin explicación.
      ...(canAccess(plan, 'ausencias') ? [
        { id: 'cal-vacaciones', icon: FiCalendar, label: 'Calendario vacaciones', to: v2('/calendario-vacaciones'), adminBadge: true },
      ] : []),
      ...(canAccess(plan, 'geolocalizacion') ? [
        { id: 'geolocalizacion', icon: FiMapPin,  label: 'Geolocalización',  to: v2('/geolocalizacion'), adminBadge: true },
      ] : []),
    ]
    const config = [
      { id: 'empleados',      icon: FiUsers,    label: 'Empleados',     to: v2('/empleados') },
      { id: 'grupos',         icon: FiLayers,   label: 'Grupos',        to: v2('/grupos') },
      { id: 'calendarios',    icon: FiCalendar, label: 'Calendarios',   to: v2('/calendarios') },
      { id: 'centros',        icon: FiMapPin,   label: 'Centros',       to: v2('/centros-trabajo') },
      { id: 'tipos-pausa',    icon: FiClock,    label: 'Tipos de pausa', to: v2('/tipos-pausa') },
      ...(canAccess(plan, 'tipos_ausencia') ? [
        { id: 'tipos-ausencia', icon: FiCalendar, label: 'Tipos de ausencia', to: v2('/tipos-ausencia') },
      ] : []),
      { id: 'auditoria',      icon: FiShield,    label: 'Auditoría',     to: v2('/auditoria') },
      { id: 'datos-empresa',  icon: FiBriefcase, label: 'Datos empresa', to: v2('/configuracion') },
    ]

    return {
      navPrimary: [...personal, ...admin],
      navSecondary: config,
    }
  }, [empleado?.rol, isAsesoriaAdmin, plan])

  // Banner superior: empresa bloqueada > trial > nada
  const topBanner = empresaBloqueada ? (
    <div className="bg-[#C13030] text-white px-4 py-2.5 text-center text-sm font-medium">
      <span className="inline-flex items-center gap-2">
        <FiLock className="h-4 w-4" />
        Licencia expirada — Modo solo lectura. Contacte con soporte para renovar.
      </span>
    </div>
  ) : (empleado?.rol === 'admin' && empresaInfo?.subscription_status === 'trialing') ? (
    <div className="px-4 pt-3 bg-[var(--bg)]"><TrialBanner empresaInfo={empresaInfo} /></div>
  ) : null

  // Plan card en el pie de la sidebar (solo admin no asesoría)
  const planCard = (empleado?.rol === 'admin' && !isAsesoriaAdmin && empresaInfo) ? {
    nombre: empresaInfo.plan ? (empresaInfo.plan.charAt(0).toUpperCase() + empresaInfo.plan.slice(1)) : null,
    usados: empresaInfo.empleados_activos,
    total: empresaInfo.slots_empleados,
    cta: { label: 'Mejorar plan', onClick: () => navigate(v2('/suscripcion')) },
  } : null

  // Slot superior del menú: notificaciones (no super_admin)
  const topActions = empleado?.rol !== 'super_admin' ? (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Notificaciones</span>
      <NotificationBell
        count={notifCount}
        notificaciones={notificaciones}
        loading={notifLoading}
        onOpen={fetchNotificaciones}
        onMarkRead={marcarLeida}
        onMarkAllRead={marcarTodasLeidas}
        onNavigate={handleNotificationNavigate}
      />
    </div>
  ) : null

  // Slot inferior del menú: avatar + dropdown con cambio password/PIN + logout
  const userActions = (
    <UserActions
      empleado={empleado}
      isAsesoriaAdmin={isAsesoriaAdmin}
      onLogout={logout}
      onCambiarPassword={() => setMostrarModalPassword(true)}
      onCambiarPin={() => setMostrarModalPin(true)}
      onActivarQuiosco={() => setMostrarModalQuiosco(true)}
    />
  )

  return (
    // Sin wrap con padding: cada página gestiona el suyo.
    // Patrón estándar: <PageHeader/> (px-7 propio) + <div className="px-7 py-6 space-y-6">…</div>
    <>
      <AppShellV2
        navPrimary={navPrimary}
        navSecondary={navSecondary}
        user={null}
        plan={planCard}
        topBanner={topBanner}
        topActions={topActions}
        userActions={userActions}
      >
        <Outlet />
      </AppShellV2>

      {/* Popups de inicio (avisos automáticos al login) */}
      {mostrarModalDiasPendientes && diasPendientes.length > 0 && (
        <ModalDiasSinJustificar
          dias={diasPendientes}
          onClose={() => setMostrarModalDiasPendientes(false)}
          onJustificado={() => {
            setMostrarModalDiasPendientes(false)
            recargarDiasPendientes()
          }}
        />
      )}

      {mostrarModalPropuestas && propuestasPendientes.length > 0 && (
        <ModalCorreccionesPropuestas
          propuestas={propuestasPendientes}
          onClose={() => setMostrarModalPropuestas(false)}
          onRespondida={() => {
            setMostrarModalPropuestas(false)
            recargarPropuestas()
          }}
        />
      )}

      {/* Modales de cambio de contraseña y PIN del usuario logueado */}
      {mostrarModalQuiosco && (
        <ActivarQuioscoModal onClose={() => setMostrarModalQuiosco(false)} />
      )}

      {mostrarModalPassword && (
        <ModalCambiarPasswordV2 onClose={() => setMostrarModalPassword(false)} />
      )}
      {mostrarModalPin && (
        <ModalCambiarPinV2
          tienePin={!!empleado?.has_pin}
          onClose={() => setMostrarModalPin(false)}
          onSuccess={() => refreshEmpleado?.()}
        />
      )}
    </>
  )
}
